import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { CheckCircle, Download, BookOpen, Mail, AlertCircle, Loader2 } from 'lucide-react';
import CorporateHeader from './CorporateHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BOOK_FILENAME = 'ava_and_maverick_go_to_school.pdf';

const AvaMaverickThankYouPage = () => {
  const [status, setStatus] = useState('loading'); // loading | success | pending | error
  const [paymentId, setPaymentId] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pid = params.get('session_id') || params.get('payment_intent');

    if (!pid) { setStatus('error'); return; }
    setPaymentId(pid);

    const endpoint = pid.startsWith('pi_')
      ? `${API_URL}/api/verify-payment/${pid}`
      : `${API_URL}/api/checkout-status/${pid}`;

    fetch(endpoint)
      .then(r => r.json())
      .then(data => setStatus(data.payment_status === 'paid' ? 'success' : 'pending'))
      .catch(() => setStatus('error'));
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-pink-500 animate-spin mx-auto mb-3" />
          <p className="text-slate-700 text-lg">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-pink-50">
        <CorporateHeader />
        <div className="pt-28 pb-20 container mx-auto px-4 max-w-xl">
          <div className="bg-white rounded-3xl shadow-xl p-8 text-center border-2 border-amber-200">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-black text-slate-800 mb-3">We couldn't verify your purchase</h2>
            <p className="text-slate-600 mb-6">
              If you just paid, give it a moment and refresh this page. Otherwise, email us and we'll help.
            </p>
            <a href="mailto:info@eBlackBookListings.com" className="inline-flex items-center gap-2 text-pink-600 font-semibold hover:text-pink-700">
              <Mail className="w-4 h-4" /> info@eBlackBookListings.com
            </a>
          </div>
        </div>
      </div>
    );
  }

  // SUCCESS
  const downloadUrl = `${API_URL}/api/download-file/${paymentId}/${BOOK_FILENAME}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-amber-50 to-pink-50">
      <CorporateHeader />
      <div className="pt-28 pb-20 container mx-auto px-4 max-w-2xl">
        <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 text-center border-4 border-pink-200" data-testid="am-success-card">
          <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>

          <h1 className="text-3xl sm:text-4xl font-black text-slate-800 mb-3" style={{ fontFamily: '"Fredoka", "Comic Sans MS", system-ui, sans-serif' }}>
            Payment Confirmed!
          </h1>
          <p className="text-slate-600 text-lg mb-8">
            Enjoy your copy of <span className="font-bold">The Adventures of Ava and Maverick Go To School</span>.
          </p>

          <a href={downloadUrl} download>
            <Button
              className="w-full bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 hover:from-pink-600 hover:via-rose-600 hover:to-amber-600 text-white font-black py-6 text-lg rounded-full shadow-xl min-h-[60px]"
              data-testid="am-download-button"
            >
              <Download className="w-5 h-5 mr-2" />
              Download Your Book (PDF)
            </Button>
          </a>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-left">
            <p className="text-amber-900 text-sm font-semibold flex items-center gap-2 mb-1">
              <BookOpen className="w-4 h-4" /> Reading Tips
            </p>
            <p className="text-amber-800 text-sm">
              Save the file to your device so you can read it offline anytime. The PDF opens in any browser, phone or tablet.
            </p>
          </div>

          <p className="text-slate-500 text-xs mt-6">
            A copy was also sent to the email you provided at checkout.<br />
            Questions? <a href="mailto:info@eBlackBookListings.com" className="text-pink-600 hover:underline">info@eBlackBookListings.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AvaMaverickThankYouPage;
